import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { IntegrationTemplate } from '../types';

const IntegrationTemplates: React.FC = () => {
  const templates: IntegrationTemplate[] = [
    {
      id: '1',
      name: 'CRM to Marketing Automation',
      description: 'Sync contacts, leads, and campaign data between your CRM and marketing platform with automated lead scoring.',
      complexity: 'Simple',
      estimatedTime: '2-3 weeks',
      icon: 'Users',
      downloadUrl: '#'
    },
    {
      id: '2',
      name: 'E-commerce to Accounting',
      description: 'Automatically sync orders, invoices, and payments from your online store to accounting software.',
      complexity: 'Moderate',
      estimatedTime: '3-4 weeks',
      icon: 'ShoppingBag',
      downloadUrl: '#'
    },
    {
      id: '3',
      name: 'Payment Gateway Integration',
      description: 'Secure payment processing with subscription management, invoicing, and automated reconciliation.',
      complexity: 'Moderate',
      estimatedTime: '4-5 weeks',
      icon: 'CreditCard',
      downloadUrl: '#'
    },
    {
      id: '4',
      name: 'Multi-Platform Inventory Sync',
      description: 'Real-time inventory synchronization across multiple sales channels with automated reorder triggers.',
      complexity: 'Complex',
      estimatedTime: '6-8 weeks',
      icon: 'Package',
      downloadUrl: '#'
    },
    {
      id: '5',
      name: 'Customer Support Integration',
      description: 'Connect helpdesk with CRM, knowledge base, and communication channels for unified support.',
      complexity: 'Moderate',
      estimatedTime: '3-5 weeks',
      icon: 'Headphones',
      downloadUrl: '#'
    },
    {
      id: '6',
      name: 'Analytics Dashboard API',
      description: 'Aggregate data from multiple sources into custom dashboards with real-time reporting and alerts.',
      complexity: 'Complex',
      estimatedTime: '5-7 weeks',
      icon: 'BarChart3',
      downloadUrl: '#'
    }
  ];

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'Simple':
        return 'bg-success/10 text-success';
      case 'Moderate':
        return 'bg-warning/10 text-warning';
      case 'Complex':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            Integration Templates & Blueprints
          </h2>
          <p className="text-lg text-text-secondary">
            Download free templates to kickstart your integration project
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div 
              key={template.id}
              className="bg-card p-6 rounded-lg border border-border hover:shadow-lg transition-all duration-fast group"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all duration-fast">
                <Icon name={template.icon} size={24} className="text-primary group-hover:text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-text-primary mb-3">
                {template.name}
              </h3>
              
              <p className="text-text-secondary text-sm mb-4 leading-relaxed">
                {template.description}
              </p>
              
              <div className="flex items-center justify-between mb-4">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getComplexityColor(template.complexity)}`}>
                  {template.complexity}
                </span>
                <div className="flex items-center space-x-1 text-text-secondary text-sm">
                  <Icon name="Clock" size={16} />
                  <span>{template.estimatedTime}</span>
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                fullWidth
                iconName="Download"
                iconPosition="left"
              >
                Download Template
              </Button>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">
            Need a custom integration solution?
          </p>
          <Button 
            variant="default" 
            size="lg"
            iconName="MessageSquare"
            iconPosition="left"
          >
            Discuss Your Requirements
          </Button>
        </div>
      </div>
    </section>
  );
};

export default IntegrationTemplates;